"use client";

import { useState, useRef } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import { HiLocationMarker } from "react-icons/hi";
import { getLocationData } from "@/data/location";
import StayEaseMap from "@/components/shared/StayEaseMap";
import { useLanguage } from "@/lib/i18n";

export default function LocationPage() {
  const { language } = useLanguage();
  const [activeLocationId, setActiveLocationId] = useState<number | null>(null);
  const locationData = getLocationData(language);
  const mapSectionRef = useRef<HTMLDivElement>(null);

  const pageContent =
    language === "id"
      ? {
          title: "Lokasi Strategis di Bali",
          subtitle:
            "KinKen Kost Elite berlokasi di area strategis Sidakarya, Denpasar Selatan. Lokasi tenang namun tetap dekat dengan pusat kota dan akses wisata.",
          superText: "Prime Neighborhood",
          viewingOnMap: "Lihat di Peta",
          backToMain: "Kembali ke Tampilan Utama",
          fullAddress: "Alamat Lengkap",
          easyAccess: "Akses Mudah:",
          easyAccessDesc:
            "Terletak di lokasi strategis dengan akses mudah ke Sanur, Renon, dan Tol Bali Mandara.",
        }
      : {
          title: "Strategic Bali Location",
          subtitle:
            "KinKen Kost Elite is located in the strategic area of Sidakarya, South Denpasar. A quiet location yet close to the city center and tourist access.",
          superText: "Prime Neighborhood",
          viewingOnMap: "Viewing on Map",
          backToMain: "Back to Main View",
          fullAddress: "Full Address",
          easyAccess: "Easy Access:",
          easyAccessDesc:
            "Located on the main road with easy access to all areas of Denpasar and South Bali.",
        };

  const handleLocationSelect = (id: number | null) => {
    setActiveLocationId(id);
    if (mapSectionRef.current) {
      // Smooth scroll to map when a location is picked OR reset
      const yOffset = -100; // Offset for navbar
      const y = mapSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="/images/location/location-hero.webp"
        superText={pageContent.superText}
      />

      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          
          {/* MAP CONTAINER */}
          <div ref={mapSectionRef} className="w-full h-[400px] md:h-[600px] relative mb-12">
            <StayEaseMap
              locations={locationData}
              activeLocationId={activeLocationId}
              onLocationSelect={setActiveLocationId}
            />
          </div>

          {/* LIST GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locationData.map((item) => (
              <div
                key={item.id}
                onClick={() => handleLocationSelect(item.id)}
                className={`flex flex-col p-6 border rounded-xl cursor-pointer transition-all duration-300 group ${
                  activeLocationId === item.id
                    ? "border-brand-gold bg-[#FAFAFA] shadow-lg transform -translate-y-1"
                    : "border-gray-100 hover:border-brand-gold/50 hover:bg-white hover:shadow-md"
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-3 rounded-full ${
                        activeLocationId === item.id
                          ? "bg-brand-gold text-white"
                          : "bg-gray-50 text-gray-400 group-hover:bg-brand-gold group-hover:text-white"
                      } transition-colors`}
                    >
                      <item.icon size={20} />
                    </div>
                    <div>
                       <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-1">
                        {item.category}
                      </span>
                       <h3 className="font-serif text-lg text-brand-charcoal leading-tight">
                        {item.name}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-4">
                  <span className="block text-xl font-bold text-brand-gold">
                    {item.dist}
                  </span>
                   {activeLocationId === item.id && (
                    <span className="flex items-center gap-1 text-[10px] text-brand-gold font-bold uppercase tracking-widest animate-pulse">
                      <HiLocationMarker size={12} /> {pageContent.viewingOnMap}
                    </span>
                  )}
                </div>

                <p className="text-gray-500 text-sm font-light leading-relaxed mb-4">
                  {item.desc}
                </p>
                
                {/* Spacer to push content up if needed, or footer area could be added here */}
                <div className="mt-auto"></div> 
              </div>
            ))}
          </div>

          {activeLocationId !== null && (
            <div className="text-center mt-12">
              <button
                onClick={() => handleLocationSelect(null)}
                className="group inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand-gold transition-colors uppercase tracking-widest font-bold"
              >
                <HiLocationMarker size={16} className="group-hover:-translate-y-1 transition-transform" /> {pageContent.backToMain}
              </button>
            </div>
          )}

          {/* FULL ADDRESS */}
          <div className="mt-20 p-10 bg-[#FAFAFA] border border-gray-100 rounded-sm text-center relative overflow-hidden">
             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent"></div>
            <h3 className="font-serif text-2xl text-brand-charcoal mb-6">
              {pageContent.fullAddress}
            </h3>
            <p className="text-gray-600 font-light mb-4 text-lg">
              <span className="font-medium text-brand-charcoal block mb-2">KinKen Kost Elite</span>
              Gg. Mawar No.8, Sidakarya
              <br />
              Denpasar Selatan, Bali 80224
              <br />
              Indonesia
            </p>
            <p className="text-gray-500 text-sm mt-6">
              <span className="font-medium">{pageContent.easyAccess}</span>{" "}
              {pageContent.easyAccessDesc}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
